import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Stack,
  IconButton,
  Chip,
  Paper,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  Zoom,
  Fade,
  useTheme,
  useMediaQuery,
  Divider,
  Tooltip
} from '@mui/material';
import {
  EmojiObjects,
  Psychology,
  Memory,
  Speed,
  TrendingUp,
  Star,
  PlayArrow,
  ArrowBack,
  Timer,
  EmojiEvents,
  Lock,
  CheckCircle,
  Whatshot,
  AutoAwesome,
  Bolt,
  Celebration,
  Rocket
} from '@mui/icons-material';

const MindGames = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameDialogOpen, setGameDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showComingSoon, setShowComingSoon] = useState(true); // Show modal immediately
  const [animationPhase, setAnimationPhase] = useState(0); // For progressive animations
  const [particleCount, setParticleCount] = useState(0); // For particle explosion effect
  const [pulseIntensity, setPulseIntensity] = useState(1); // For dynamic pulsing

  // Animated phases for the modal with smoother, less flashing effects
  useEffect(() => {
    if (showComingSoon) {
      const phases = [
        { delay: 500, phase: 1 },
        { delay: 1200, phase: 2 },
        { delay: 2000, phase: 3 },
        { delay: 2800, phase: 4 },
        { delay: 3800, phase: 5 },
        { delay: 4800, phase: 6 }
      ];

      phases.forEach(({ delay, phase }) => {
        setTimeout(() => setAnimationPhase(phase), delay);
      });

      // Reduced particle explosion effect
      setTimeout(() => {
        setParticleCount(25); // Reduced from 50 to 25
      }, 1500); // Delayed slightly more

      // Gentler pulse intensity with slower changes
      const pulseInterval = setInterval(() => {
        setPulseIntensity(prev => prev === 1 ? 1.15 : 1); // Reduced from 1.3 to 1.15
      }, 3000); // Increased from 2000 to 3000

      return () => clearInterval(pulseInterval);
    }
  }, [showComingSoon]);

  // Mock mind games data
  const mindGames = [
    {
      id: 1,
      title: "Memory Palace",
      description: "Strengthen your memory by building mental palaces and remembering sequences",
      category: "memory",
      difficulty: "beginner",
      duration: "5-10 min",
      points: 50,
      highScore: 1250,
      playCount: 23,
      icon: "🏰",
      color: "#4CAF50",
      features: ["Visual Memory", "Spatial Thinking", "Concentration"]
    },
    {
      id: 2,
      title: "Focus Flow",
      description: "Test and improve your sustained attention with gradually increasing challenges",
      category: "focus",
      difficulty: "intermediate",
      duration: "10-15 min",
      points: 75,
      highScore: 890,
      playCount: 15,
      icon: "🎯",
      color: "#2196F3",
      features: ["Sustained Attention", "Cognitive Control", "Mental Stamina"]
    },
    {
      id: 3,
      title: "Pattern Detective",
      description: "Identify hidden patterns and sequences to boost logical thinking",
      category: "logic",
      difficulty: "intermediate",
      duration: "8-12 min",
      points: 80,
      highScore: 2340,
      playCount: 31,
      icon: "🔍",
      color: "#FF9800",
      features: ["Pattern Recognition", "Logical Reasoning", "Problem Solving"]
    },
    {
      id: 4,
      title: "Word Weaver",
      description: "Enhance vocabulary and verbal fluency through word association games",
      category: "language",
      difficulty: "beginner",
      duration: "5-8 min",
      points: 45,
      highScore: 560,
      playCount: 18,
      icon: "📝",
      color: "#9C27B0",
      features: ["Vocabulary", "Verbal Fluency", "Creative Thinking"]
    },
    {
      id: 5,
      title: "Speed Math",
      description: "Quick mathematical challenges to improve processing speed",
      category: "math",
      difficulty: "intermediate",
      duration: "3-5 min",
      points: 60,
      highScore: 0,
      playCount: 0,
      icon: "🔢",
      color: "#FF5722",
      features: ["Processing Speed", "Mental Math", "Quick Thinking"],
      locked: true
    },
    {
      id: 6,
      title: "Mindful Attention",
      description: "Combine mindfulness with cognitive training for enhanced awareness",
      category: "mindfulness",
      difficulty: "advanced",
      duration: "15-20 min",
      points: 100,
      highScore: 1100,
      playCount: 8,
      icon: "🧘",
      color: "#795548",
      features: ["Mindful Awareness", "Emotional Regulation", "Present Moment Focus"]
    },
    {
      id: 7,
      title: "Color Cognition",
      description: "Visual processing and reaction time challenges with colors and shapes",
      category: "visual",
      difficulty: "beginner",
      duration: "4-6 min",
      points: 40,
      highScore: 780,
      playCount: 12,
      icon: "🎨",
      color: "#E91E63",
      features: ["Visual Processing", "Reaction Time", "Color Recognition"]
    },
    {
      id: 8,
      title: "Dual N-Back",
      description: "Advanced working memory training with simultaneous audio and visual tasks",
      category: "memory",
      difficulty: "advanced",
      duration: "12-18 min",
      points: 120,
      highScore: 0,
      playCount: 0,
      icon: "🧠",
      color: "#607D8B",
      features: ["Working Memory", "Dual Task", "Cognitive Flexibility"],
      locked: true
    }
  ];

  const categories = [
    { id: 'all', label: 'All Games', icon: '🎮' },
    { id: 'memory', label: 'Memory', icon: '🧠' },
    { id: 'focus', label: 'Focus', icon: '🎯' },
    { id: 'logic', label: 'Logic', icon: '🔍' },
    { id: 'language', label: 'Language', icon: '📝' },
    { id: 'math', label: 'Math', icon: '🔢' },
    { id: 'mindfulness', label: 'Mindfulness', icon: '🧘' },
    { id: 'visual', label: 'Visual', icon: '🎨' }
  ];

  const filteredGames = selectedCategory === 'all' 
    ? mindGames 
    : mindGames.filter(game => game.category === selectedCategory);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return '#4CAF50';
      case 'intermediate': return '#FF9800';
      case 'advanced': return '#F44336';
      default: return '#757575';
    }
  };

  const handlePlayGame = (game) => {
    if (game.locked) {
      alert('This game will be unlocked as you progress through other games!');
      return;
    }
    setSelectedGame(game);
    setGameDialogOpen(true);
  };

  const handleStartGame = () => {
    setGameDialogOpen(false);
    // In a real app, this would launch the actual game
    alert(`Starting ${selectedGame.title}! This would launch the interactive game.`);
  };

  return (
    <Box 
      sx={{ 
        bgcolor: '#fafbfc', 
        minHeight: '100vh',
        cursor: 'pointer'
      }}
      onClick={(e) => {
        // Check if the clicked element is interactive
        const isInteractive = e.target.closest('button, a, input, select, textarea, [role="button"], .MuiCard-root, .MuiIconButton-root, .MuiButton-root');
        
        // Only navigate if not clicking on interactive elements
        if (!isInteractive) {
          navigate('/conversation');
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        bgcolor: 'white', 
        borderBottom: '1px solid #e0e0e0', 
        mb: 3,
        position: 'relative',
        zIndex: 1300 // Higher than modal backdrop (1200)
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
            <Tooltip title="Back to Conversation" arrow>
              <IconButton 
                onClick={() => navigate('/conversation')} 
                sx={{ 
                  mr: 2,
                  position: 'relative',
                  zIndex: 1301, // Even higher to ensure visibility
                  '&:hover': {
                    backgroundColor: 'rgba(156, 39, 176, 0.1)',
                  }
                }}
              >
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <EmojiObjects sx={{ mr: 1, color: '#9C27B0' }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
              Mind Games
            </Typography>
          </Box>
          <Typography variant="subtitle1" color="text.secondary" sx={{ pb: 2 }}>
            Train your brain with fun, scientifically-designed cognitive exercises
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 4 }}>
        {/* Stats Overview */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                {mindGames.filter(g => g.playCount > 0).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Games Played
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#FF9800' }}>
                {mindGames.reduce((total, game) => total + game.playCount, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Sessions
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#9C27B0' }}>
                {mindGames.reduce((total, game) => total + (game.playCount > 0 ? game.points : 0), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Points Earned
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Category Filter */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Game Categories</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {categories.map((category) => (
              <Chip
                key={category.id}
                label={`${category.icon} ${category.label}`}
                onClick={() => setSelectedCategory(category.id)}
                variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                color={selectedCategory === category.id ? 'primary' : 'default'}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </Paper>

        {/* Games Grid */}
        <Grid container spacing={3}>
          {filteredGames.map((game) => (
            <Grid item xs={12} md={6} lg={4} key={game.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  },
                  opacity: game.locked ? 0.7 : 1
                }}
              >
                {game.locked && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1
                    }}
                  >
                    <Lock sx={{ color: '#757575' }} />
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Game Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: game.color,
                        mr: 2,
                        width: 56,
                        height: 56,
                        fontSize: '1.8rem'
                      }}
                    >
                      {game.icon}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {game.title}
                      </Typography>
                      <Chip 
                        label={game.difficulty}
                        size="small"
                        sx={{ 
                          bgcolor: getDifficultyColor(game.difficulty),
                          color: 'white',
                          fontWeight: 500,
                          mt: 0.5
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Description */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 2, minHeight: 48 }}
                  >
                    {game.description}
                  </Typography>

                  {/* Features */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Trains:
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      {game.features.map((feature, index) => (
                        <Chip
                          key={index}
                          label={feature}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Stats */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: game.color }}>
                          {game.highScore || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          High Score
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: game.color }}>
                          {game.playCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Sessions
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Game Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Timer sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {game.duration}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Star sx={{ fontSize: 16, mr: 0.5, color: '#FFD700' }} />
                      <Typography variant="body2" color="text.secondary">
                        {game.points} pts
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={game.locked ? <Lock /> : <PlayArrow />}
                    onClick={() => handlePlayGame(game)}
                    disabled={game.locked}
                    sx={{ 
                      bgcolor: game.locked ? '#ccc' : game.color,
                      '&:hover': {
                        bgcolor: game.locked ? '#ccc' : game.color,
                        opacity: 0.8
                      },
                      '&.Mui-disabled': {
                        bgcolor: '#ccc',
                        color: '#fff'
                      }
                    }}
                  >
                    {game.locked ? 'Locked' : 'Play Game'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {filteredGames.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <EmojiObjects sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No games found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try selecting a different category or check back for new games.
            </Typography>
          </Paper>
        )}

        {/* Game Start Dialog */}
        <Dialog open={gameDialogOpen} onClose={() => setGameDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            {selectedGame && (
              <>
                <Avatar sx={{ bgcolor: selectedGame.color, mr: 2 }}>
                  {selectedGame.icon}
                </Avatar>
                Ready to play {selectedGame.title}?
              </>
            )}
          </DialogTitle>
          <DialogContent>
            {selectedGame && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedGame.description}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    This game will help you improve:
                  </Typography>
                  <Stack spacing={0.5}>
                    {selectedGame.features.map((feature, index) => (
                      <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircle sx={{ fontSize: 16, mr: 1, color: '#4CAF50' }} />
                        {feature}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Duration: {selectedGame.duration} • Earn up to {selectedGame.points} points
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGameDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleStartGame}
              sx={{ bgcolor: selectedGame?.color }}
            >
              Start Game
            </Button>
          </DialogActions>
        </Dialog>

        {/* EPIC DOPAMINE-INDUCING COMING SOON MODAL */}
        <Dialog
          open={showComingSoon}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 6,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 15%, #f093fb 30%, #f5576c 45%, #4facfe 60%, #00f2fe 75%, #667eea 100%)',
              backgroundSize: '600% 600%',
              animation: 'epicGradientShift 3s ease infinite, modalPulse 4s ease-in-out infinite',
              border: '4px solid rgba(255, 255, 255, 0.4)',
              boxShadow: `
                0 0 60px rgba(102, 126, 234, 0.8),
                0 0 100px rgba(245, 87, 108, 0.6),
                0 0 140px rgba(79, 172, 254, 0.4),
                inset 0 0 60px rgba(255, 255, 255, 0.1)
              `,
              overflow: 'hidden',
              position: 'relative',
              transform: `scale(${pulseIntensity})`,
              transition: 'transform 0.5s ease-in-out',
              maxHeight: '95vh',
              height: { xs: '95vh', sm: '90vh', md: '85vh' },
              margin: { xs: 1, sm: 2, md: 3 },
              '@keyframes epicGradientShift': {
                '0%': { backgroundPosition: '0% 50%' },
                '25%': { backgroundPosition: '100% 25%' },
                '50%': { backgroundPosition: '50% 100%' },
                '75%': { backgroundPosition: '25% 0%' },
                '100%': { backgroundPosition: '0% 50%' }
              },
              '@keyframes modalPulse': {
                '0%, 100%': { 
                  boxShadow: `
                    0 0 60px rgba(102, 126, 234, 0.6),
                    0 0 100px rgba(245, 87, 108, 0.4),
                    0 0 140px rgba(79, 172, 254, 0.3)
                  `
                },
                '50%': { 
                  boxShadow: `
                    0 0 80px rgba(102, 126, 234, 0.8),
                    0 0 120px rgba(245, 87, 108, 0.6),
                    0 0 160px rgba(79, 172, 254, 0.4),
                    0 0 180px rgba(255, 215, 0, 0.2)
                  `
                }
              },
              '@keyframes megaPulse': {
                '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
                '50%': { transform: 'scale(1.05) rotate(180deg)' }
              },
              '@keyframes hyperFloat': {
                '0%': { transform: 'translateY(0px) rotateX(0deg)' },
                '50%': { transform: 'translateY(-10px) rotateX(180deg)' },
                '100%': { transform: 'translateY(0px) rotateX(360deg)' }
              },
              '@keyframes epicGlow': {
                '0%, 100%': { 
                  filter: 'brightness(1) hue-rotate(0deg)',
                  textShadow: '0 0 15px rgba(255, 255, 255, 0.6)'
                },
                '50%': { 
                  filter: 'brightness(1.2) hue-rotate(180deg)',
                  textShadow: '0 0 25px rgba(255, 215, 0, 0.8)'
                }
              },
              '@keyframes explosiveSparkle': {
                '0%': { 
                  opacity: 0, 
                  transform: 'scale(0) rotate(0deg)',
                  filter: 'brightness(1)'
                },
                '50%': { 
                  opacity: 0.8, 
                  transform: 'scale(1.3) rotate(180deg)',
                  filter: 'brightness(1.3) saturate(1.3)'
                },
                '100%': { 
                  opacity: 0, 
                  transform: 'scale(0) rotate(360deg)',
                  filter: 'brightness(1.5)'
                }
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `
                  radial-gradient(circle at 20% 20%, rgba(255, 215, 0, 0.3) 0%, transparent 40%),
                  radial-gradient(circle at 80% 80%, rgba(255, 105, 180, 0.3) 0%, transparent 40%),
                  radial-gradient(circle at 50% 50%, rgba(0, 255, 255, 0.2) 0%, transparent 60%),
                  linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)
                `,
                backdropFilter: 'blur(30px)',
                zIndex: 1,
                animation: 'epicGradientShift 4s ease infinite'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '200%',
                height: '200%',
                transform: 'translate(-50%, -50%)',
                background: 'conic-gradient(from 0deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                animation: 'megaPulse 6s linear infinite',
                zIndex: 0
              }
            }
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              backdropFilter: 'blur(20px)',
              background: `
                radial-gradient(circle at 30% 30%, rgba(102, 126, 234, 0.3) 0%, transparent 50%),
                radial-gradient(circle at 70% 70%, rgba(245, 87, 108, 0.3) 0%, transparent 50%)
              `,
              animation: 'epicGradientShift 8s ease infinite'
            }
          }}
          disableEscapeKeyDown
          hideBackdrop={false}
        >
          <Box sx={{ 
            position: 'relative', 
            zIndex: 3, 
            p: { xs: 1.5, sm: 2, md: 3 }, 
            textAlign: 'center', 
            color: 'white',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            
            {/* MEGA PARTICLE EXPLOSION - Reduced intensity */}
            {Array.from({ length: particleCount }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  width: Math.random() * 8 + 3, // Reduced from 12+4 to 8+3
                  height: Math.random() * 8 + 3,
                  backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FF69B4', '#00FFFF'][Math.floor(Math.random() * 6)],
                  borderRadius: Math.random() > 0.5 ? '50%' : '0%',
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animation: `
                    explosiveSparkle ${Math.random() * 3 + 2}s ease-in-out infinite,
                    hyperFloat ${Math.random() * 5 + 4}s ease-in-out infinite
                  `,
                  animationDelay: `${Math.random() * 4}s`,
                  zIndex: 0,
                  filter: 'blur(0.5px)',
                  boxShadow: `0 0 15px currentColor`, // Reduced glow
                  opacity: 0.7 // Reduced opacity
                }}
              />
            ))}

            {/* FLOATING ENERGY ORBS - Gentler */}
            {Array.from({ length: 6 }).map((_, i) => ( // Reduced from 8 to 6
              <Box
                key={`orb-${i}`}
                sx={{
                  position: 'absolute',
                  width: 15 + Math.random() * 20, // Reduced size
                  height: 15 + Math.random() * 20,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1'][i % 4]}, transparent)`,
                  top: `${20 + Math.random() * 60}%`,
                  left: `${10 + Math.random() * 80}%`,
                  animation: `hyperFloat ${4 + Math.random() * 2}s ease-in-out infinite`, // Slower
                  animationDelay: `${i * 0.8}s`, // More spread out
                  zIndex: 1,
                  opacity: 0.5, // Reduced opacity
                  filter: 'blur(1px)',
                  boxShadow: `0 0 25px currentColor` // Reduced glow
                }}
              />
            ))}

            {/* MEGA BRAIN ICON WITH REDUCED EFFECTS */}
            <Zoom in={animationPhase >= 1} timeout={1000}>
              <Box sx={{ mb: { xs: 2, sm: 3, md: 4 }, position: 'relative' }}>
                {/* Energy rings around brain */}
                {Array.from({ length: 3 }).map((_, i) => (
                  <Box
                    key={`ring-${i}`}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: 150 + i * 40,
                      height: 150 + i * 40,
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '50%',
                      transform: 'translate(-50%, -50%)',
                      animation: `megaPulse ${2 + i * 0.5}s ease-in-out infinite`,
                      animationDelay: `${i * 0.3}s`,
                      zIndex: 0
                    }}
                  />
                ))}
                
                <Avatar
                  sx={{
                    width: { xs: 80, sm: 100, md: 120 },
                    height: { xs: 80, sm: 100, md: 120 },
                    bgcolor: 'transparent',
                    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1))',
                    border: '6px solid rgba(255, 255, 255, 0.6)',
                    margin: '0 auto',
                    animation: animationPhase >= 2 ? 'megaPulse 1.5s ease-in-out infinite' : 'none',
                    backdropFilter: 'blur(30px)',
                    fontSize: { xs: '3rem', sm: '3.5rem', md: '4rem' },
                    boxShadow: `
                      0 0 60px rgba(255, 255, 255, 0.8),
                      inset 0 0 40px rgba(255, 255, 255, 0.2)
                    `,
                    position: 'relative',
                    zIndex: 2,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'conic-gradient(from 0deg, #FFD700, #FF6B6B, #4ECDC4, #45B7D1, #FFD700)',
                      borderRadius: '50%',
                      animation: 'megaPulse 3s linear infinite',
                      zIndex: -1,
                      filter: 'blur(10px)'
                    }
                  }}
                >
                  🧠
                </Avatar>
              </Box>
            </Zoom>

            {/* EXPLOSIVE TITLE WITH MULTIPLE EFFECTS */}
            <Fade in={animationPhase >= 2} timeout={1200}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  mb: { xs: 1.5, sm: 2, md: 3 },
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                  background: 'linear-gradient(45deg, #FFD700 0%, #FF6B6B 25%, #4ECDC4 50%, #45B7D1 75%, #FF69B4 100%)',
                  backgroundSize: '400% 400%',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'epicGlow 2s ease-in-out infinite, epicGradientShift 3s ease infinite',
                  letterSpacing: '-0.03em',
                  lineHeight: 0.9,
                  textTransform: 'uppercase',
                  position: 'relative',
                  '&::before': {
                    content: '"COMING SOON"',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'blur(4px)',
                    opacity: 0.7,
                    zIndex: -1
                  }
                }}
              >
                COMING SOON
              </Typography>
            </Fade>

            {/* MIND-BLOWING SUBTITLE WITH MULTI-LAYER EFFECTS */}
            <Fade in={animationPhase >= 3} timeout={1000}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
                  color: 'rgba(255, 255, 255, 0.95)',
                  textShadow: `
                    0 0 20px rgba(255, 255, 255, 0.8),
                    0 0 40px rgba(255, 215, 0, 0.6),
                    0 0 60px rgba(255, 105, 180, 0.4)
                  `,
                  animation: 'epicGlow 3s ease-in-out infinite',
                  letterSpacing: '0.05em',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80%',
                    height: 3,
                    background: 'linear-gradient(90deg, transparent, #FFD700, #FF6B6B, #4ECDC4, transparent)',
                    borderRadius: 2,
                    animation: 'epicGradientShift 2s ease infinite'
                  }
                }}
              >
                Brain Mental Wellness Games That Heal & Strengthen
              </Typography>
            </Fade>

            {/* EXPLOSIVE DESCRIPTION WITH RAINBOW EFFECTS */}
            <Fade in={animationPhase >= 4} timeout={1200}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  lineHeight: 1.4,
                  color: 'rgba(255, 255, 255, 0.9)',
                  textShadow: '0 0 15px rgba(255, 255, 255, 0.6)',
                  fontWeight: 500,
                  background: 'linear-gradient(45deg, #ffffff, #FFD700, #ffffff, #4ECDC4, #ffffff)',
                  backgroundSize: '200% 200%',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'epicGradientShift 4s ease infinite',
                  position: 'relative'
                }}
              >
                ★ Revolutionary games that boost your mental health while training your mind! 
                <br />
                ★ Reduce anxiety, improve focus, and build resilience through play! ★
              </Typography>
            </Fade>

            {/* MEGA CELEBRATION ICONS WITH EXPLOSIVE ANIMATIONS */}
            <Zoom in={animationPhase >= 5} timeout={1000}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: { xs: 1, sm: 1.5, md: 2 }, 
                mb: { xs: 2, sm: 2.5, md: 3 },
                position: 'relative'
              }}>
                {['💚', '🧠', '🌟', '✨', '🎯', '💎', '⚡', '🌈'].map((icon, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                      animation: `
                        explosiveSparkle ${1 + Math.random()}s ease-in-out infinite,
                        hyperFloat ${2 + Math.random() * 2}s ease-in-out infinite
                      `,
                      animationDelay: `${index * 0.2}s`,
                      filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.8))',
                      cursor: 'default',
                      userSelect: 'none',
                      position: 'relative',
                      zIndex: 2,
                      '&:hover': {
                        transform: 'scale(1.5) rotate(360deg)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    {icon}
                  </Typography>
                ))}
              </Box>
            </Zoom>

            {/* MEGA CELEBRATION ICONS WITH EXPLOSIVE ANIMATIONS */}
            <Zoom in={animationPhase >= 5} timeout={1000}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: { xs: 2, md: 4 }, 
                mb: 4,
                position: 'relative'
              }}>
                {['💚', '🧠', '🌟', '✨', '🦋', '♢', '⚡', '◆'].map((icon, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      animation: `
                        explosiveSparkle ${1 + Math.random()}s ease-in-out infinite,
                        hyperFloat ${2 + Math.random() * 2}s ease-in-out infinite
                      `,
                      animationDelay: `${index * 0.2}s`,
                      filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.8))',
                      cursor: 'default',
                      userSelect: 'none',
                      position: 'relative',
                      zIndex: 2,
                      '&:hover': {
                        transform: 'scale(1.5) rotate(360deg)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    {icon}
                  </Typography>
                ))}
              </Box>
            </Zoom>

            {/* ULTIMATE HYPE NOTIFICATION */}
            <Zoom in={animationPhase >= 6} timeout={1500}>
              <Box
                sx={{
                  mt: { xs: 2, sm: 2.5, md: 3 },
                  p: { xs: 2, sm: 2.5, md: 3 },
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.3), rgba(139, 195, 74, 0.3), rgba(156, 204, 101, 0.3))',
                  borderRadius: 4,
                  border: '2px solid rgba(76, 175, 80, 0.4)',
                  backdropFilter: 'blur(40px)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                    '@keyframes slideShine': {
                      '0%': { left: '-100%' },
                      '100%': { left: '100%' }
                    }
                  }
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                    color: '#ffffff',
                    textShadow: '0 0 25px rgba(255, 255, 255, 0.9)',
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  ★ LAUNCHING SOON - TRANSFORM YOUR MENTAL HEALTH THROUGH PLAY! ★
                </Typography>
              </Box>
            </Zoom>

            {/* FLOATING MENTAL HEALTH EMOJIS */}
            {animationPhase >= 4 && Array.from({ length: 8 }).map((_, i) => (
              <Box
                key={`emoji-${i}`}
                sx={{
                  position: 'absolute',
                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
                  top: `${Math.random() * 80 + 10}%`,
                  left: `${Math.random() * 80 + 10}%`,
                  animation: `hyperFloat ${3 + Math.random() * 2}s ease-in-out infinite`,
                  animationDelay: `${Math.random() * 3}s`,
                  zIndex: 1,
                  opacity: 0.8,
                  filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.6))',
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}
              >
                {['♡', '○', '◊', '◆', '★', '✦', '◉', '●'][i]}
              </Box>
            ))}

          </Box>
        </Dialog>

        {/* Game Details Dialog */}
        <Dialog
          open={gameDialogOpen}
          onClose={() => setGameDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {selectedGame?.title}
              </Typography>
              <Chip
                label={selectedGame?.difficulty}
                size="small"
                sx={{
                  bgcolor: getDifficultyColor(selectedGame?.difficulty),
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography paragraph>
              {selectedGame?.description}
            </Typography>
            <Typography variant="h6" gutterBottom>
              What you'll train:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {selectedGame?.skills.map((skill, index) => (
                <Chip key={index} label={skill} size="small" variant="outlined" />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Estimated time: {selectedGame?.estimatedTime}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGameDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleStartGame}
              variant="contained"
              startIcon={<PlayArrow />}
            >
              Start Game
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default MindGames;
